/**
 * Shared utility functions for date field styling and validation logic
 * Used in both createPurchaseOrder and viewPurchaseOrder components
 */

/**
 * Determines if an item is a pre-planned purchase type
 * @param {string} purchaseType - The purchase type (EVERYDAY, PRIVATE_BRAND, SEASONAL, OPPORTUNISTIC)
 * @returns {boolean} - True if the purchase type is pre-planned
 */
export const isPrePlannedItem = (purchaseType) => {
    return purchaseType === 'EVERYDAY' || purchaseType === 'PRIVATE_BRAND' || purchaseType === 'SEASONAL';
};

/**
 * Gets the styling for expected expiration date field
 * @param {boolean} hasPrintedExpDate - Whether the item has a printed expiration date
 * @param {string} purchaseType - The purchase type
 * @returns {Object} - Style object for the field
 */
export const getExpirationDateFieldStyle = (hasPrintedExpDate, purchaseType) => {
    if (!hasPrintedExpDate) {
        // Field doesn't apply - muted
        return { background: 'lightgrey', opacity: '50%' };
    }
    if (purchaseType === 'OPPORTUNISTIC') {
        // User needs to fill it in - normal/inviting style
        return {};
    }
    // Auto-populated (pre-planned items) - muted style
    return { background: 'lightgrey', opacity: '50%' };
};

/**
 * Gets the styling for expected production date field
 * @param {boolean} hasPrintedProdDate - Whether the item has a printed production date
 * @param {string} purchaseType - The purchase type
 * @returns {Object} - Style object for the field
 */
export const getProductionDateFieldStyle = (hasPrintedProdDate, purchaseType) => {
    if (!hasPrintedProdDate) {
        // Field doesn't apply - muted
        return { background: 'lightgrey', opacity: '50%' };
    }
    if (purchaseType === 'OPPORTUNISTIC') {
        // User needs to fill it in - normal/inviting style
        return {};
    }
    // Auto-populated (pre-planned items) - muted style
    return { background: 'lightgrey', opacity: '50%' };
};

/**
 * Determines if the expiration date field should be disabled
 * @param {boolean} hasPrintedExpDate - Whether the item has a printed expiration date
 * @param {string} purchaseType - The purchase type
 * @returns {boolean} - True if the field should be disabled
 */
export const isExpirationDateDisabled = (hasPrintedExpDate, purchaseType) => {
    return hasPrintedExpDate && purchaseType && isPrePlannedItem(purchaseType);
};

/**
 * Determines if the production date field should be disabled
 * @param {boolean} hasPrintedProdDate - Whether the item has a printed production date
 * @param {string} purchaseType - The purchase type
 * @returns {boolean} - True if the field should be disabled
 */
export const isProductionDateDisabled = (hasPrintedProdDate, purchaseType) => {
    return hasPrintedProdDate && purchaseType && isPrePlannedItem(purchaseType);
};

/**
 * Determines if the expiration date field is required
 * @param {boolean} hasPrintedExpDate - Whether the item has a printed expiration date
 * @param {string} purchaseType - The purchase type
 * @returns {boolean} - True if the field is required
 */
export const isExpirationDateRequired = (hasPrintedExpDate, purchaseType) => {
    return hasPrintedExpDate && purchaseType === 'OPPORTUNISTIC';
};

/**
 * Determines if the production date field is required
 * @param {boolean} hasPrintedProdDate - Whether the item has a printed production date
 * @param {string} purchaseType - The purchase type
 * @returns {boolean} - True if the field is required
 */
export const isProductionDateRequired = (hasPrintedProdDate, purchaseType) => {
    return hasPrintedProdDate && purchaseType === 'OPPORTUNISTIC';
};
